
import React, { useState } from 'react';
import PageTransition from '@/components/layout/PageTransition';
import StatCard from '@/components/dashboard/StatCard';
import { Wifi, WifiOff, AlertTriangle, Users, Network, Activity } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import NetworkGraph from '@/components/dashboard/NetworkGraph';
import MeshNetworkGraph from '@/components/dashboard/MeshNetworkGraph';
import DeviceTable from '@/components/dashboard/DeviceTable';
import AlertCard from '@/components/dashboard/AlertCard';
import { useMobile } from '@/hooks/use-mobile';
import { mockNetworks } from '@/data/mockNetworks';

const Index = () => {
  const isMobile = useMobile();
  // State to toggle between regular network and mesh network view
  const [showMeshView, setShowMeshView] = useState(false);
  
  // Count active and inactive networks
  const activeNetworks = mockNetworks.filter(n => n.status === 'active').length;
  const inactiveNetworks = mockNetworks.filter(n => n.status === 'inactive').length;
  const maintenanceNetworks = mockNetworks.filter(n => n.status === 'maintenance').length;
  
  return (
    <PageTransition>
      <div className="section-padding">
        <div className="mb-6">
          <h1 className="text-2xl font-bold overflow-wrap-anywhere">Particle Mesh Command Center</h1>
          <p className="text-muted-foreground overflow-wrap-anywhere">
            Manage and monitor your mesh network
          </p>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <StatCard 
            title="Total Networks" 
            value={mockNetworks.length.toString()} 
            icon={Network}
            description={!isMobile ? "Across 3 locations" : undefined}
            trend={!isMobile ? { value: 12, isPositive: true } : undefined}
            inlineTrend={!isMobile}
            className="h-auto min-h-[140px] md:min-h-[160px]"
          />
          <StatCard 
            title="Active Networks" 
            value={activeNetworks.toString()} 
            icon={Wifi}
            description={!isMobile ? `${(activeNetworks/mockNetworks.length*100).toFixed(1)}% of total networks` : undefined}
            className="h-auto min-h-[140px] md:min-h-[160px]"
          />
          <StatCard 
            title="Inactive Networks" 
            value={inactiveNetworks.toString()} 
            icon={WifiOff}
            description={!isMobile ? `${(inactiveNetworks/mockNetworks.length*100).toFixed(1)}% of total networks` : undefined}
            className="h-auto min-h-[140px] md:min-h-[160px]"
          />
          <StatCard 
            title="Maintenance Networks" 
            value={maintenanceNetworks.toString()} 
            icon={AlertTriangle}
            description={!isMobile ? `${(maintenanceNetworks/mockNetworks.length*100).toFixed(1)}% of total networks` : undefined}
            className="h-auto min-h-[140px] md:min-h-[160px]"
          />
        </div>
        
        {/* Network Traffic */}
        <div className="glass rounded-xl p-3 sm:p-5 mb-6 sm:mb-8 card-auto-height">
          <div className="card-header-responsive">
            <h2 className="text-lg font-semibold overflow-hidden text-ellipsis">
              {showMeshView ? "Mesh Network Topology" : "Network Traffic"}
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              <button 
                onClick={() => setShowMeshView(!showMeshView)}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
              >
                <Network className="h-4 w-4 flex-shrink-0" />
                <span className="overflow-hidden text-ellipsis">
                  {showMeshView ? "Show Traffic Chart" : "Show Mesh Topology"}
                </span>
              </button>
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <Activity className="h-4 w-4 flex-shrink-0" />
                <span className="overflow-hidden text-ellipsis">Live Data</span>
              </div>
            </div>
          </div>
          <div className={showMeshView ? "h-auto min-h-[250px] md:min-h-[350px]" : ""}>
            {showMeshView ? <MeshNetworkGraph /> : <NetworkGraph />}
          </div>
        </div>
        
        {/* Content Tabs */}
        <Tabs defaultValue="devices" className="mb-8">
          <TabsList className="grid w-full grid-cols-2 md:w-auto md:inline-flex">
            <TabsTrigger value="devices">Devices</TabsTrigger>
            <TabsTrigger value="alerts">Alerts</TabsTrigger>
          </TabsList>
          
          <TabsContent value="devices" className="mt-4">
            <div className="glass rounded-xl p-3 sm:p-5 card-auto-height">
              <div className="card-header-responsive">
                <h2 className="text-lg font-semibold overflow-hidden text-ellipsis">Device Status</h2>
              </div>
              <DeviceTable />
            </div>
          </TabsContent>
          
          <TabsContent value="alerts" className="mt-4">
            <div className="glass rounded-xl p-3 sm:p-5 card-auto-height">
              <div className="card-header-responsive">
                <h2 className="text-lg font-semibold overflow-hidden text-ellipsis">Recent Alerts</h2>
              </div>
              <div className="space-y-4">
                <AlertCard 
                  type="critical"
                  title="Connection Lost"
                  message="Garage Door (DEV003) has been offline for 3 hours."
                  time="12:42 PM"
                />
                <AlertCard 
                  type="warning"
                  title="Weak Signal"
                  message="Bedroom Light (DEV004) is experiencing connectivity issues."
                  time="1:15 PM"
                />
                <AlertCard 
                  type="info"
                  title="Firmware Update Available"
                  message="New firmware available for 3 devices."
                  time="9:30 AM"
                />
                <AlertCard 
                  type="warning"
                  title="High CPU Usage"
                  message="Living Room Hub (DEV001) is running at 85% CPU usage."
                  time="11:04 AM"
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageTransition>
  );
};

export default Index;
